/* ==========================================================================
   EMI Calculator behaviour
   - Two-way binding between slider and editable value input
   - Merchant-defined min / max / default / step (read from data-* attributes)
   - Robust edge-case handling (bad config, empty input, rate 0, term 0, etc.)
   - EMI = P * r * (1+r)^n / ((1+r)^n - 1)
   ========================================================================== */
(function () {
  "use strict";

  /* ---------- helpers ---------- */

  function toNumber(value) {
    if (value === null || value === undefined) return NaN;
    var cleaned = String(value).replace(/[^0-9.\-]/g, "");
    // keep only the first dot
    var parts = cleaned.split(".");
    if (parts.length > 2) cleaned = parts[0] + "." + parts.slice(1).join("");
    var n = parseFloat(cleaned);
    return isNaN(n) ? NaN : n;
  }

  function clamp(value, min, max) {
    if (value < min) return min;
    if (value > max) return max;
    return value;
  }

  function decimalsOf(step) {
    var s = String(step);
    var i = s.indexOf(".");
    return i === -1 ? 0 : s.length - i - 1;
  }

  // snap a value onto the step grid, anchored at min
  function snap(value, min, step) {
    if (!(step > 0)) return value;
    var steps = Math.round((value - min) / step);
    return min + steps * step;
  }

  function makeFormatter(numberFormat, maxFrac) {
    var locale = numberFormat === "indian" ? "en-IN" : "en-US";
    try {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: maxFrac,
      });
    } catch (e) {
      return {
        format: function (n) {
          return String(n);
        },
      };
    }
  }

  /* ---------- per-slider setup ---------- */

  function buildSlider(el, root) {
    var min = toNumber(el.dataset.min);
    var max = toNumber(el.dataset.max);
    var step = toNumber(el.dataset.step);
    var def = toNumber(el.dataset.default);

    // --- edge cases on config ---
    if (isNaN(min)) min = 0;
    if (isNaN(max)) max = min + 100;
    if (min > max) {
      var t = min;
      min = max;
      max = t;
    } // swap if reversed
    if (isNaN(step) || step <= 0) step = 1;
    if (step > max - min) step = max - min || 1;
    if (isNaN(def)) def = min;
    def = clamp(def, min, max);

    var range = el.querySelector("[data-emi-input]");
    var input = el.querySelector("[data-emi-value]");
    var minEl = el.querySelector("[data-emi-min]");
    var maxEl = el.querySelector("[data-emi-max]");

    var prefix = el.dataset.prefix || "";
    var suffix = el.dataset.suffix || "";
    var dec = decimalsOf(step);
    var fmt = makeFormatter(root._numberFormat, dec);

    var slider = {
      el: el,
      role: el.dataset.role,
      min: min,
      max: max,
      step: step,
      value: def,
      format: function (v) {
        return fmt.format(v);
      },
    };

    function setFill() {
      var pct = max > min ? ((slider.value - min) / (max - min)) * 100 : 0;
      range.style.setProperty("--_fill", clamp(pct, 0, 100) + "%");
    }

    function sizeInput() {
      var len = (input.value || "").length;
      input.style.width = Math.max(len + 0.5, 2) + "ch";
    }

    function paintInput() {
      input.value = fmt.format(slider.value);
      sizeInput();
    }

    // configure native range
    range.min = String(min);
    range.max = String(max);
    range.step = String(step);
    range.value = String(def);

    // static scale labels (thin space between symbol and number)
    var affixGap = prefix ? ' ' : '';
    if (minEl) minEl.textContent = prefix + affixGap + fmt.format(min) + suffix;
    if (maxEl) maxEl.textContent = prefix + affixGap + fmt.format(max) + suffix;

    paintInput();
    setFill();

    /* --- slider drag --- */
    range.addEventListener("input", function () {
      slider.value = clamp(toNumber(range.value), min, max);
      paintInput();
      setFill();
      root._recalc();
    });

    /* --- typing in the value badge (live, no reformat to keep caret) --- */
    input.addEventListener("input", function () {
      var raw = toNumber(input.value);
      sizeInput();
      if (isNaN(raw)) return; // wait until something valid is typed
      var v = clamp(raw, min, max);
      slider.value = v;
      range.value = String(v);
      setFill();
      root._recalc();
    });

    /* --- commit on blur / Enter: snap, clamp, reformat --- */
    function commit() {
      var raw = toNumber(input.value);
      if (isNaN(raw)) raw = def;
      var v = clamp(snap(raw, min, step), min, max);
      // rounding guard for float noise
      v = parseFloat(v.toFixed(dec));
      slider.value = v;
      range.value = String(v);
      paintInput();
      setFill();
      root._recalc();
    }

    input.addEventListener("blur", commit);
    input.addEventListener("keydown", function (e) {
      if (e.key === "Enter") {
        e.preventDefault();
        input.blur();
      }
    });

    return slider;
  }

  /* ---------- EMI math ---------- */

  function calcEMI(principal, annualRate, years) {
    var n = Math.round(years * 12); // months
    if (!(n > 0) || !(principal > 0)) {
      return { emi: 0, total: 0, interest: 0 };
    }
    var r = annualRate / 12 / 100; // monthly rate
    var emi;
    if (r <= 0) {
      emi = principal / n; // zero-interest fallback
    } else {
      var pow = Math.pow(1 + r, n);
      emi = (principal * r * pow) / (pow - 1);
    }
    if (!isFinite(emi) || emi < 0) emi = 0;
    var total = emi * n;
    var interest = total - principal;
    if (interest < 0) interest = 0;
    return { emi: emi, total: total, interest: interest };
  }

  /* ---------- root (one calculator instance) ---------- */

  function initRoot(root) {
    if (!root || root._emiReady) return;
    root._emiReady = true;

    root._numberFormat = root.dataset.numberFormat || "international";
    var currency = root.dataset.currency || "";
    var money = makeFormatter(root._numberFormat, 0);

    var sliders = [];
    root.querySelectorAll("[data-emi-slider]").forEach(function (el) {
      sliders.push(buildSlider(el, root));
    });

    var out = {};
    root.querySelectorAll("[data-emi-result]").forEach(function (el) {
      out[el.dataset.emiResult] = el;
    });

    function valueFor(role) {
      for (var i = 0; i < sliders.length; i++) {
        if (sliders[i].role === role) return sliders[i].value;
      }
      return null;
    }

    function money$(v) {
      return currency + money.format(Math.round(v));
    }

    root._recalc = function () {
      var principal = valueFor("principal");
      var rate = valueFor("rate");
      var term = valueFor("term");

      // if any required role is missing, show neutral zeros
      if (principal === null || rate === null || term === null) {
        if (out.emi) out.emi.textContent = money$(0);
        if (out.total) out.total.textContent = money$(0);
        if (out.interest) out.interest.textContent = money$(0);
        return;
      }

      var res = calcEMI(principal, rate, term);
      // keep displayed interest consistent with displayed total
      var totalRounded = Math.round(res.total);
      var interestRounded = Math.max(0, totalRounded - Math.round(principal));

      if (out.emi) out.emi.textContent = money$(res.emi);
      if (out.total)
        out.total.textContent = currency + money.format(totalRounded);
      if (out.interest)
        out.interest.textContent = currency + money.format(interestRounded);
    };

    root._recalc();
  }

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll("[data-emi-root]");
    nodes.forEach(initRoot);
  }

  /* ---------- boot ---------- */

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAll();
    });
  } else {
    initAll();
  }

  /* ---------- Shopify theme editor ---------- */
  document.addEventListener("shopify:section:load", function (e) {
    initAll(e.target);
  });
})();
